import * as React from 'react';

import type { CatalogVersionDto } from '@/api/catalog-client';
import { useCatalogVersions } from '@/api/use-catalog';
import { usePublishCatalogVersion } from '@/api/use-catalog-edition';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { catalogVersionStateLabel, formatCatalogDate } from '@/lib/catalog-labels';

// =============================================================================
// catalog-versions-screen.tsx — A-08 browser des versions du catalogue (S8-D2).
// =============================================================================
// BC catalog, écran ADMIN (P-05 : monde admin, séparé du monde fournisseur). Browser
// des versions du catalogue : numéro, état (badge + libellé), dates de création /
// publication / archivage. Une seule action d'écriture ADDITIVE (BL-FRONT-BACK §2.4) :
// « Promouvoir » une version au statut Brouillon (DRAFT→PUBLISHED), via confirm-dialog.
// L'archivage (PUBLISHED→ARCHIVED) reste HORS périmètre (§3.9). L'édition/l'upload sont
// portés par leurs écrans dédiés (A-09/A-10).
//
// L'AUTORITÉ (RBAC AdminSessionGuard, transitions d'état DRAFT→PUBLISHED→ARCHIVED) est
// l'API ; l'UI ne fait que lire et afficher. catalog = référentiel GLOBAL : AUCUNE
// donnée nominative, AUCUNE PII (uniquement des métadonnées de version).
//
// WCAG 2.1 AA : <table> avec <caption>, <th scope> ; états rendus en LIBELLÉ texte
// (badge = renfort couleur, jamais seul porteur d'info) ; messages role="status"/
// role="alert" ; navigation clavier native.
//
// S1-NAV (câblage A-08 → A-09/A-10) : la navigation d'ÉCRITURE est câblée par prop
// (adaptateur routeur), l'écran reste testable hors routeur. « Éditer » (par version)
// ouvre A-09 (édition profil, /versions/:id/edit) ; « Importer un fichier Excel » ouvre
// A-10 (upload + dry-run, /uploads). L'AUTORITÉ (édition réservée DRAFT, inv.2) est l'API.

export interface CatalogVersionsScreenProps {
  /** Ouvre l'édition A-09 d'une version (navigation portée par l'adaptateur routeur). */
  readonly onEditVersion: (catalogVersionId: string) => void;
  /** Ouvre l'import Excel A-10 (navigation portée par l'adaptateur routeur). */
  readonly onImportExcel: () => void;
}

export function CatalogVersionsScreen({
  onEditVersion,
  onImportExcel,
}: CatalogVersionsScreenProps): React.JSX.Element {
  return (
    <div className="space-y-8">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            Versions du catalogue
          </h1>
          <p className="text-muted-foreground">
            Référentiel global des versions du catalogue. Éditez une version brouillon
            ou importez un fichier Excel pour créer une nouvelle version.
          </p>
        </div>
        <Button type="button" onClick={onImportExcel}>
          Importer un fichier Excel
        </Button>
      </header>

      <CatalogVersionsTable onEditVersion={onEditVersion} />
    </div>
  );
}

function CatalogVersionsTable({
  onEditVersion,
}: {
  readonly onEditVersion: (catalogVersionId: string) => void;
}): React.JSX.Element {
  const list = useCatalogVersions();
  const publish = usePublishCatalogVersion();
  // Version en attente de confirmation de promotion (null = aucune modale ouverte).
  const [promoting, setPromoting] = React.useState<CatalogVersionDto | null>(null);
  // CONFIRMATION TEXTUELLE (DEC-20260722-9 pt.3) : la publication DRAFT→PUBLISHED (irréversible pour la
  // version précédente, archivée atomiquement) exige de RESAISIR le numéro de version — pas un simple
  // clic. Garde-fou anti-fausse-manœuvre en plus de la modale.
  const [confirmText, setConfirmText] = React.useState('');

  const openPromote = React.useCallback((row: CatalogVersionDto): void => {
    publish.reset();
    setConfirmText('');
    setPromoting(row);
  }, [publish]);

  const closePromote = React.useCallback((): void => {
    setPromoting(null);
    setConfirmText('');
  }, []);

  const confirmMatches =
    promoting !== null && confirmText.trim() === promoting.versionNumber;

  const onConfirmPromote = React.useCallback((): void => {
    if (promoting === null || confirmText.trim() !== promoting.versionNumber) {
      return;
    }
    publish.mutate(promoting.catalogVersionId, {
      onSuccess: () => closePromote(),
    });
  }, [promoting, confirmText, publish, closePromote]);

  if (list.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement des versions…
      </p>
    );
  }
  if (list.isError) {
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement : {list.error.message}
      </p>
    );
  }
  if (list.data.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucune version de catalogue pour le moment.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {publish.isError ? (
        <p role="alert" className="text-sm text-destructive">
          Échec de la publication : {publish.error.message}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border">
      <table className="ds-table w-full border-collapse text-sm">
        <caption className="sr-only">
          Liste des versions du catalogue (numéro de version, état, date de création,
          date de publication, date d'archivage)
        </caption>
        <thead>
          <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
            <th scope="col" className="px-4 py-3 font-medium">
              Version
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              État
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Créée le
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Publiée le
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              Archivée le
            </th>
            <th scope="col" className="px-4 py-3 font-medium">
              <span className="sr-only">Action</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {list.data.map((row) => {
            const state = catalogVersionStateLabel(row.state);
            return (
              <tr
                key={row.catalogVersionId}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-4 py-3 align-top font-mono">
                  {row.versionNumber}
                </td>
                <td className="px-4 py-3 align-top">
                  {/* Badge = renfort ; le LIBELLÉ texte porte l'information. */}
                  <Badge variant={state.variant}>{state.label}</Badge>
                </td>
                <td className="px-4 py-3 align-top">
                  {formatCatalogDate(row.createdAt)}
                </td>
                <td className="px-4 py-3 align-top">
                  {row.publishedAt === null ? (
                    <span className="text-muted-foreground" aria-label="Non publiée">
                      —
                    </span>
                  ) : (
                    formatCatalogDate(row.publishedAt)
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  {row.archivedAt === null ? (
                    <span className="text-muted-foreground" aria-label="Non archivée">
                      —
                    </span>
                  ) : (
                    formatCatalogDate(row.archivedAt)
                  )}
                </td>
                <td className="px-4 py-3 align-top text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onEditVersion(row.catalogVersionId)}
                      aria-label={`Éditer la version ${row.versionNumber}`}
                    >
                      Éditer
                    </Button>
                    {/* Promotion DRAFT→PUBLISHED : action disponible UNIQUEMENT sur un Brouillon. */}
                    {row.state === 'DRAFT' ? (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => openPromote(row)}
                        disabled={publish.isPending}
                        aria-label={`Promouvoir la version ${row.versionNumber}`}
                      >
                        Promouvoir
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      <ConfirmDialog
        open={promoting !== null}
        title="Publier cette version du catalogue ?"
        description={
          promoting === null
            ? ''
            : `La version « ${promoting.versionNumber} » (Brouillon) sera publiée et deviendra la version officielle active. La version publiée précédente, s'il en existe une, sera automatiquement archivée. Cette action est sensible : confirmez en resaisissant le numéro de version.`
        }
        confirmLabel={publish.isPending ? 'Publication…' : 'Publier'}
        confirmDisabled={publish.isPending || !confirmMatches}
        onConfirm={onConfirmPromote}
        onCancel={closePromote}
      >
        {promoting !== null ? (
          <div className="space-y-2">
            <Label htmlFor="publish-confirm-input">
              Saisissez « {promoting.versionNumber} » pour confirmer
            </Label>
            <Input
              id="publish-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              aria-describedby="publish-confirm-hint"
            />
            <p id="publish-confirm-hint" className="text-xs text-muted-foreground">
              La publication est confirmée uniquement si le numéro saisi correspond exactement.
            </p>
          </div>
        ) : null}
      </ConfirmDialog>
    </div>
  );
}
